/** The Designs screen (docs/ui-decisions.md, "Designs"): your designs, a
 *  spec sheet to edit one, and a live preview the server bakes as you go;
 *  titans' footprints drawn hex by hex. */

import { v31 } from "../state/features";
import { markRetired, retired } from "../state/designs";
import { For, Show, createEffect, createSignal, on, type JSX } from "solid-js";
import { createStore, reconcile, unwrap } from "solid-js/store";
import { setTip } from "../ui/tooltip";

/*
 * What each choice does in play, from the sim's rules (engineering.rs:
 * `Spec::bake_with`; species.rs: `Training`): the hover tips.
 */
const COMPONENT_TIPS: Record<string, string> = {
  Armour: "Plates over the hull: a hit must get through their thickness (times the material's hardness) to hurt, and they add hit points. Heavier the thicker, the denser and the bigger the frame.",
  Weapon: "One a design. Its calibre sets damage, range (2 + calibre/25 hexes), penetration (calibre times the barrel's hardness), reload and rounds carried; heavier with calibre. Indirect lobs shells at a hex from behind cover, reloading half as fast.",
  Sensor: "Sight in hexes (a species' eyes add to it): how far it sees and spots for others. A little weight.",
  Cargo: "Carries rounds for others, to resupply units away from a depot. A tenth of a tonne per 4 rounds.",
  Construction: "Builds: work a tick on your construction sites (Field engineering adds 5).",
  Prospector: "Surveys ore: EM on the move (roughly how much, at once), soil stopped 2 minutes (which ores), a core drill stopped 20 minutes (exactly; vehicles only).",
  Medic: "Heals troops beside it: hit points a tick.",
  Repair: "Repairs machines beside it: hit points a tick.",
};
const MATERIAL_TIPS: Record<string, string> = {
  Steel: "Steel: the baseline (weight, strength and hardness 100).",
  Iron: "Iron: as heavy as steel, 60% its strength, 70% its hardness. Made of steel.",
  Copper: "Copper: 14% heavier than steel, 45% its strength, 35% its hardness. Made of copper.",
  "Light alloy": "Light alloy: a third of steel's weight, 80% its strength, 60% its hardness. Made of light alloy (aluminium with magnesium), a tonne a tonne. Needs the Light alloy research.",
  Aluminium: "Aluminium: about a third of steel's weight, 65% its strength, 45% its hardness. Made of aluminium (from bauxite).",
  "Armour steel": "Armour steel: as heavy as steel, 120% its strength, 170% its hardness. Made of armour steel (steel with nickel, chromium or manganese).",
  "Tungsten carbide": "Tungsten carbide: 60% heavier than steel, 90% its strength, 260% its hardness. Made of tungsten carbide.",
  Bronze: "Bronze: 10% heavier than steel, 55% its strength, 50% its hardness. Made of bronze (copper with tin).",
};
const LOCOMOTION_TIPS: Record<string, string> = {
  Wheels: "Wheels: the fastest (up to 700), at full efficiency; takes roads.",
  Tracks: "Tracks: up to 500, at 80% efficiency; takes roads.",
  Legs: "Legs: up to 300, at 60% efficiency.",
};
const POWER_TIPS: Record<string, string> = {
  Combustion: "Combustion: a tonne of engine per 100 kW. A titan burns 5 fuel per hex a minute.",
  Electric: "Electric: half the weight per kW (a tonne per 200 kW), built of magnets and batteries rather than steel. A titan draws a battery a minute per 10 hexes (or part of 10).",
};
const TRAINING_TIPS: Record<string, string> = {
  Marksmanship: "Hit more often: 70% instead of 55%.",
  Endurance: "Move further for less: 20% faster.",
  FieldEngineering: "Build faster: 5 more construction work a tick (if it builds at all).",
  AntiArmour: "Anti-armour drill: half as much penetration again, so rifles hurt armour.",
};
/** Each regimen's build time (species::TRAINING_PCT). */
const TRAINING_TIME = " Each regimen adds half an infantryman's build time.";
/** The stats a hover compares, and their names. */
const DELTA_STATS: [string, string][] = [
  ["hp", "health"],
  ["speed", "speed"],
  ["armor", "armour"],
  ["damage", "damage"],
  ["pen", "penetration"],
  ["range", "range"],
  ["sight", "sight"],
  ["cargo", "cargo"],
  ["build", "building"],
  ["medic", "healing"],
  ["repair", "repair"],
];

const MATERIALS = ["Steel", "Iron", "Copper", "Light alloy", "Aluminium", "Armour steel", "Tungsten carbide", "Bronze"];
const LOCOMOTION = ["Wheels", "Tracks", "Legs"];
const POWER = ["Combustion", "Electric"];
const PROSPECTS: [string, string][] = [
  ["Em", "EM survey"],
  ["Soil", "Soil sampler"],
  ["Core", "Core drill"],
];
const TRAINING: [string, string][] = [
  ["Marksmanship", "Marksmanship"],
  ["Endurance", "Endurance"],
  ["FieldEngineering", "Field engineering"],
  ["AntiArmour", "Anti-armour"],
];
const LEGS = ["Walker", "Drill", "Freight", "Rail"];
/** What each brush does (the server's LegKind: only Walker does anything
 *  yet; the rest are planned and have no effect). */
const LEG_TIPS: Record<string, string> = {
  Hull: "Hull: add or remove a hex of the titan's footprint (a slot each)",
  Walker: "Walker: carries the titan; its walking ground",
  Drill: "Drill: planned, no effect yet",
  Freight: "Freight: planned, no effect yet",
  Rail: "Rail: planned, no effect yet",
};
const PLANNED_LEGS = new Set(["Drill", "Freight", "Rail"]);
/** Footprint hexes reachable: a hexagon of this radius around the centre. */
const RADIUS = 3;

type Leg = { at: [number, number]; kind: string };
/** A component, as the server writes it (one key: its kind). */
export type Component =
  | { Armour: { material: string; thickness: number } }
  | { Weapon: { calibre: number; barrel: string; indirect: boolean } }
  | { Sensor: { sight: number } }
  | { Cargo: { capacity: number } }
  | { Construction: { rate: number } }
  | { Prospector: { kind: string } }
  | { Medic: { rate: number } }
  | { Repair: { rate: number } };

/** Each kind, as the add buttons offer it, and what a new one starts as. */
const KINDS: [string, string, () => Component][] = [
  ["Armour", "Armour plate", () => ({ Armour: { material: "Steel", thickness: 15 } })],
  ["Weapon", "Weapon", () => ({ Weapon: { calibre: 12, barrel: "Steel", indirect: false } })],
  ["Sensor", "Sensor", () => ({ Sensor: { sight: 8 } })],
  ["Cargo", "Cargo", () => ({ Cargo: { capacity: 40 } })],
  ["Construction", "Construction kit", () => ({ Construction: { rate: 10 } })],
  ["Prospector", "Prospecting", () => ({ Prospector: { kind: "Soil" } })],
  ["Medic", "Medic kit", () => ({ Medic: { rate: 3 } })],
  ["Repair", "Repair kit", () => ({ Repair: { rate: 3 } })],
];
const kindOf = (c: Component) => Object.keys(c)[0];
/** A slot's count for a hull (mirrors `Spec::slots`, until the server's
 *  preview says). */
const slotsFor = (s: { class: string; frame_mass: number; titan: { tiles: unknown[] } | null }) =>
  s.class === "Infantry" ? 3 : s.class === "Vehicle" ? Math.min(8, 2 + Math.floor(s.frame_mass / 8)) : (s.titan?.tiles.length ?? 0);
export interface Spec {
  name: string;
  class: "Infantry" | "Vehicle" | "Titan";
  frame: string;
  frame_mass: number;
  locomotion: string;
  power: string;
  output: number;
  /** What's in its slots (ferro-sim's `engineering::Component`). */
  components: Component[];
  body: unknown;
  training: string[];
  titan: { tiles: [number, number][]; legs: Leg[] } | null;
}

const hexagon = (): [number, number][] => {
  const out: [number, number][] = [];
  for (let q = -RADIUS; q <= RADIUS; q++)
    for (let r = -RADIUS; r <= RADIUS; r++)
      if (Math.abs(q + r) <= RADIUS) out.push([q, r]);
  return out;
};

/** Templates that work untouched (no research needed but a titan's). */
const TEMPLATES: Record<Spec["class"], () => Spec> = {
  Infantry: () => ({
    name: "Riflemen",
    class: "Infantry",
    frame: "Steel",
    frame_mass: 1,
    locomotion: "Foot",
    power: "Muscle",
    output: 0,
    components: [{ Weapon: { calibre: 8, barrel: "Steel", indirect: false } }, { Sensor: { sight: 6 } }],
    body: null,
    training: [],
    titan: null,
  }),
  Vehicle: () => ({
    name: "Armoured car",
    class: "Vehicle",
    frame: "Steel",
    frame_mass: 8,
    locomotion: "Wheels",
    power: "Combustion",
    output: 150,
    components: [
      { Weapon: { calibre: 20, barrel: "Steel", indirect: false } },
      { Armour: { material: "Steel", thickness: 15 } },
      { Sensor: { sight: 8 } },
    ],
    body: null,
    training: [],
    titan: null,
  }),
  Titan: () => ({
    name: "Titan",
    class: "Titan",
    frame: "Steel",
    frame_mass: 200,
    locomotion: "Legs",
    power: "Combustion",
    output: 4000,
    components: [
      { Weapon: { calibre: 150, barrel: "Steel", indirect: false } },
      { Armour: { material: "Steel", thickness: 120 } },
      { Sensor: { sight: 12 } },
    ],
    body: null,
    training: [],
    titan: {
      tiles: [
        [0, 0],
        [1, 0],
        [1, -1],
        [0, -1],
        [-1, 0],
        [-1, 1],
        [0, 1],
      ],
      legs: [
        { at: [1, 0], kind: "Walker" },
        { at: [-1, 1], kind: "Walker" },
        { at: [0, -1], kind: "Walker" },
      ],
    },
  }),
};

interface Props {
  state: () => Record<string, unknown> | null;
  send: (command: Record<string, unknown>) => void;
  bake: (spec: object) => Promise<Record<string, unknown>>;
  /** A short message at the bottom of the screen. */
  toast?: (text: string) => void;
}

// The design being drawn up, kept outside the screen so it survives the
// screen being built anew (registering re-sends the world).
const [spec, setSpec] = createStore<Spec>(TEMPLATES.Vehicle());

// A design about to be retired (its × clicked once; a second click does it).
const [retiring, setRetiring] = createSignal<number | null>(null);

// Sent and not yet among the registered designs: the button waits (a second
// click would register it twice). Kept outside the screen: registering
// re-sends the world, which builds the screen anew.
const [sent, setSent] = createSignal<{ name: string; at: number } | null>(null);

export function DesignScreen(p: Props): JSX.Element {
  const load = (s: Spec) => setSpec(reconcile(structuredClone(s)));
  const [preview, setPreview] = createSignal<Record<string, unknown> | null>(
    null,
  );
  // Ask the server for a preview after each edit (the latest answer wins).
  let asked = 0;
  createEffect(
    on(
      () => JSON.stringify(spec),
      (json) => {
        const n = ++asked;
        const timer = setTimeout(() => {
          p.bake(JSON.parse(json)).then((r) => n === asked && setPreview(r));
        }, 120);
        return () => clearTimeout(timer);
      },
    ),
  );

  // Registered and not retired (retired ones leave at once).
  const listed = () =>
    (p.state()?.designs as
      { id: number; name: string; spec: Spec }[] | undefined) ?? [];
  const mine = () => listed().filter((d) => !retired().has(d.id));
  const goods = () => (p.state()?.goods as string[] | undefined) ?? [];
  // What the player has researched: the capabilities their known techs unlock.
  const has = () => {
    const r = p.state()?.research as
      | {
          techs: { id: number; unlocks: Record<string, string>[] }[];
          known: number[];
        }
      | undefined;
    const out = new Set<string>();
    for (const id of r?.known ?? [])
      for (const u of r?.techs[id]?.unlocks ?? []) out.add(Object.values(u)[0]);
    return out;
  };
  const stats = () => preview()?.stats as Record<string, number> | undefined;
  const needs = () => (preview()?.needs as string[] | undefined) ?? [];
  const missing = () => needs().filter((n) => !has().has(n));
  const error = () => preview()?.error as string | undefined;
  // The same design already registered (whatever its name), if any: a
  // design is what it is, so a second one under another name is refused
  // here rather than piling up.
  const canon = (v: unknown): string =>
    v === null || typeof v !== "object"
      ? JSON.stringify(v ?? null)
      : Array.isArray(v)
        ? `[${v.map(canon).join(",")}]`
        : `{${Object.keys(v as object)
            .filter((k) => k !== "name")
            .sort()
            .map((k) => `${k}:${canon((v as Record<string, unknown>)[k])}`)
            .join(",")}}`;
  const twin = () => {
    const mine0 = canon(unwrap(spec));
    return mine().find((d) => canon(d.spec) === mine0) ?? null;
  };
  createEffect(() => {
    const s = sent();
    if (!s) return;
    const done = mine().find((d) => d.name === s.name);
    if (done) {
      setSent(null);
      p.toast?.(`Registered: ${s.name}. Barracks can make it now.`);
    }
  });
  const register = () => {
    if (sent() || twin()) return;
    p.send({ type: "register", spec: unwrap(spec) });
    setSent({ name: spec.name.trim(), at: performance.now() });
    // No word back in ten seconds: let it be tried again.
    setTimeout(() => {
      if (sent()) {
        setSent(null);
        p.toast?.("No word back from the server: try registering again.");
      }
    }, 10000);
  };

  const infantry = () => spec.class === "Infantry";
  // Bodies a soldier can have: a person (the commander's own people, if
  // they chose one), or a robot once Robotics is known.
  const sameBody = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  const bodies = () => {
    const pop = p.state()?.population as { species?: string | null; chose?: string | null } | undefined;
    const own = pop?.chose ?? pop?.species ?? null;
    const robot = has().has("Robotics");
    return [
      { label: "Human", value: null as unknown, locked: false, tip: "A person: baseline hit points and speed; trained from your people." },
      ...(own && own !== "Human" ? [{ label: own, value: { Species: own } as unknown, locked: false, tip: `One of your ${own} people.` }] : []),
      {
        label: "Robot",
        value: "Robot" as unknown,
        locked: !robot,
        tip: robot
          ? "A machine: baseline hit points and speed, no people needed; costs 20 more steel and 10 copper. Machines: repaired by repair crews, not healed by medics."
          : "Research Robotics first (Engineering). A machine soldier: needs no people. Machines: repaired by repair crews, not healed by medics.",
      },
    ];
  };
  const titan = () => spec.class === "Titan";
  const setClass = (c: Spec["class"]) => {
    if (c !== spec.class) load({ ...TEMPLATES[c](), name: spec.name });
  };

  const select = (
    value: () => string,
    set: (v: string) => void,
    options: string[],
    tips?: Record<string, string>,
  ) => (
    <select
      value={value()}
      onChange={(e) => set(e.currentTarget.value)}
      ref={(el) => tips && setTip(el, () => [tips[value()] ?? "", ...options.filter((o) => o !== value() && tips[o]).map((o) => tips[o])].join("\n"))}
    >
      <For each={options}>{(o) => <option value={o}>{o}</option>}</For>
    </select>
  );
  // A hover's "what it would change": the spec with the change baked by the
  // server, against the preview, in a line ("+40 armour · −12 speed").
  const [deltas, setDeltas] = createSignal<Record<string, string>>({});
  const askDelta = (key: string, change: (s: Spec) => Spec) => {
    if (key in deltas()) return;
    const base = preview();
    p.bake(change(structuredClone(unwrap(spec)) as Spec)).then((r) => {
      const a = base?.stats as Record<string, number> | undefined;
      const b = r.stats as Record<string, number> | undefined;
      let text = r.error ? `Can't: ${r.error}` : "";
      if (a && b) {
        const parts = DELTA_STATS.map(([k, name]) => [name, (b[k] ?? 0) - (a[k] ?? 0)] as const)
          .filter(([, d]) => d !== 0)
          .map(([name, d]) => `${d > 0 ? "+" : "−"}${Math.abs(d)} ${name}`);
        const mass = ((r.mass as number) ?? 0) - ((base?.mass as number) ?? 0);
        if (mass) parts.push(`${mass > 0 ? "+" : "−"}${Math.abs(mass)} t`);
        text = parts.join(" · ") || "No change to its stats";
      }
      setDeltas((d) => ({ ...d, [key]: text }));
    });
  };
  // The cache goes when the design changes.
  createEffect(on(() => JSON.stringify(spec), () => setDeltas({})));
  /** A hover tip with the change it'd make (asked for on hover). */
  const deltaTip = (el: HTMLElement, key: string, text: string, change: (s: Spec) => Spec) => {
    setTip(el, () => {
      const d = deltas()[key];
      return d ? `${text}\n${d}` : text;
    });
    el.addEventListener("pointerenter", () => askDelta(key, change));
  };
  const number = (
    value: () => number,
    set: (n: number) => void,
    unit: string,
    step = 1,
  ) => (
    <span class="unit-input">
      <input
        type="number"
        min="0"
        step={step}
        value={value()}
        onInput={(e) =>
          set(Math.max(0, Math.floor(Number(e.currentTarget.value) || 0)))
        }
      />
      <span class="muted">{unit}</span>
    </span>
  );
  const toggle = (
    on: () => boolean,
    set: (v: boolean) => void,
    label: string,
    tip?: (el: HTMLElement) => void,
  ) => (
    <button class="chip" aria-pressed={on()} onClick={() => set(!on())} ref={(el) => tip?.(el)}>
      {label}
    </button>
  );
  // A component's controls, editing it in place.
  // By path, so the component stays the same object (and its inputs keep
  // focus while typing).
  const set = (k: number, kind: string, field: string, v: unknown) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (setSpec as any)("components", k, kind, field, v);
  const controls = (c: Component, k: number): JSX.Element => {
    const kind = kindOf(c);
    // Its fields, loosely: each case reads the ones its kind has.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const v = (c as unknown as Record<string, Record<string, any>>)[kind];
    switch (kind) {
      case "Armour":
        return (
          <>
            {select(() => v.material, (x) => set(k, kind, "material", x), MATERIALS, MATERIAL_TIPS)}
            {number(() => v.thickness, (x) => set(k, kind, "thickness", x), "mm")}
          </>
        );
      case "Weapon":
        return (
          <>
            {number(() => v.calibre, (x) => set(k, kind, "calibre", x), "mm")}
            {select(() => v.barrel, (x) => set(k, kind, "barrel", x), MATERIALS, MATERIAL_TIPS)}
            {toggle(() => v.indirect, (x) => set(k, kind, "indirect", x), "Indirect", (el) => setTip(el, () => "Lobs shells at a hex from behind cover instead of firing at what it sees; reloads half as fast."))}
          </>
        );
      case "Sensor":
        return number(() => v.sight, (x) => set(k, kind, "sight", x), "hexes");
      case "Cargo":
        return number(() => v.capacity, (x) => set(k, kind, "capacity", x), "rounds", 10);
      case "Construction":
        return number(() => v.rate, (x) => set(k, kind, "rate", x), "work/tick");
      case "Prospector":
        return select(
          () => PROSPECTS.find(([id]) => id === v.kind)?.[1] ?? v.kind,
          (x) => set(k, kind, "kind", PROSPECTS.find(([, l]) => l === x)?.[0] ?? x),
          PROSPECTS.map(([, l]) => l),
          {
            "EM survey": "EM survey: on the move, at once; roughly how much ore lies around (±40%), not which.",
            "Soil sampler": "Soil sampler: stopped for 2 minutes; which ores, roughly (±60% each).",
            "Core drill": "Core drill: stopped for 20 minutes; exactly how much of each ore. Vehicles only; heavy.",
          },
        );
      case "Medic":
        return number(() => v.rate, (x) => set(k, kind, "rate", x), "hp/tick to troops");
      case "Repair":
        return number(() => v.rate, (x) => set(k, kind, "rate", x), "hp/tick to machines");
      default:
        return null;
    }
  };
  const slots = () => (preview()?.slots as number | undefined) ?? slotsFor(spec);

  // The rows' labels explain the hull (engineering.rs: hit points, slots,
  // speed).
  const ROW_TIPS: Record<string, string> = {
    Class: "Infantry: a soldier with three slots and training. Vehicle: a frame and engine, two slots and one more per 8 t. Titan: drawn hex by hex, a slot a hex.",
    Frame: "The hull: its tonnes times the material's strength give its hit points; every 8 t adds a slot; and it's weight to move.",
    Kit: "What a soldier carries: its weight slows them.",
    Drive: "How it moves. Speed is the engine's output (kW) times the drive's efficiency, over the whole mass, up to the drive's top speed.",
    Training: "Drills for infantry, each one more thing it does better.",
  };
  const row = (label: string, children: JSX.Element) => (
    <div class="spec-row">
      <span class="row-label" data-tip={ROW_TIPS[label]}>
        {label}
      </span>
      <div class="spec-controls">{children}</div>
    </div>
  );

  // The footprint: click toggles a hex, right-click cycles its leg.
  const tiles = () => spec.titan?.tiles ?? [];
  const legAt = (q: number, r: number) =>
    spec.titan?.legs.find((l) => l.at[0] === q && l.at[1] === r);
  const hasTile = (q: number, r: number) =>
    tiles().some(([a, b]) => a === q && b === r);
  const toggleTile = (q: number, r: number) => {
    if (!spec.titan || (q === 0 && r === 0)) return;
    if (hasTile(q, r)) {
      setSpec("titan", "tiles", (t) =>
        t.filter(([a, b]) => a !== q || b !== r),
      );
      setSpec("titan", "legs", (l) =>
        l.filter((x) => x.at[0] !== q || x.at[1] !== r),
      );
    } else {
      setSpec("titan", "tiles", (t) => [...t, [q, r] as [number, number]]);
    }
  };
  const cycleLeg = (q: number, r: number) => {
    if (!spec.titan || !hasTile(q, r)) return;
    const now = legAt(q, r);
    const next = now ? LEGS[LEGS.indexOf(now.kind) + 1] : LEGS[0];
    setSpec("titan", "legs", (l) => [
      ...l.filter((x) => x.at[0] !== q || x.at[1] !== r),
      ...(next ? [{ at: [q, r] as [number, number], kind: next }] : []),
    ]);
  };
  // The footprint's brush: the hull (add or remove hexes) or a leg kind
  // (placed on a hull hex; the same leg again takes it off).
  const [brush, setBrush] = createSignal<string>("Hull");
  const paint = (q: number, r: number) => {
    const b = brush();
    if (b === "Hull") return toggleTile(q, r);
    if (!spec.titan || !hasTile(q, r)) return;
    const same = legAt(q, r)?.kind === b;
    setSpec("titan", "legs", (l) => [
      ...l.filter((x) => x.at[0] !== q || x.at[1] !== r),
      ...(same ? [] : [{ at: [q, r] as [number, number], kind: b }]),
    ]);
  };
  const SIZE = 22;
  const px = (q: number, r: number) => [
    Math.sqrt(3) * SIZE * (q + r / 2),
    1.5 * SIZE * r,
  ];
  const corners = (x: number, y: number) =>
    Array.from({ length: 6 }, (_, k) => {
      const a = (Math.PI / 180) * (60 * k - 30);
      return `${(x + (SIZE - 1.5) * Math.cos(a)).toFixed(1)},${(y + (SIZE - 1.5) * Math.sin(a)).toFixed(1)}`;
    }).join(" ");

  const stat = (label: string, value: string | number | undefined) => (
    <Show when={value !== undefined && value !== 0 && value !== "0"}>
      <div class="stat">
        <span class="muted">{label}</span>
        <span>{value}</span>
      </div>
    </Show>
  );

  return (
    <section class="depth-screen designs" aria-label="Designs">
      <div class="design-list">
        <h2>New</h2>
        <div class="chips">
          <For each={["Infantry", "Vehicle", "Titan"] as Spec["class"][]}>
            {(c) => (
              <button class="chip" onClick={() => load(TEMPLATES[c]())}>
                {c}
              </button>
            )}
          </For>
        </div>
        <h2>Yours</h2>
        <Show
          when={mine().length}
          fallback={<p class="muted small">None registered yet.</p>}
        >
          <For each={mine()}>
            {(d) => (
              <div class="design-row">
                <button class="design-item" onClick={() => load(d.spec)}>
                  {d.name}
                </button>
                {/* Retire it: asked once, in place. Its units keep serving. */}
                <button
                  class="ghost design-retire"
                  classList={{ armed: retiring() === d.id }}
                  data-tip={retiring() === d.id ? "Click again to retire it" : "Retire: barracks can't make it any more; its units keep serving"}
                  onClick={() => {
                    if (retiring() !== d.id) {
                      setRetiring(d.id);
                      setTimeout(() => retiring() === d.id && setRetiring(null), 4000);
                      return;
                    }
                    setRetiring(null);
                    markRetired(d.id);
                    p.send({ type: "retire", design: d.id });
                    p.toast?.(`Retired: ${d.name}`);
                  }}
                >
                  {retiring() === d.id ? "Retire?" : "×"}
                </button>
              </div>
            )}
          </For>
        </Show>
      </div>

      <div class="spec-sheet">
        {row(
          "Name",
          <input
            class="name-input"
            value={spec.name}
            onInput={(e) => setSpec("name", e.currentTarget.value)}
          />,
        )}
        {row(
          "Class",
          <div class="segmented" role="radiogroup" aria-label="Class">
            <For each={["Infantry", "Vehicle", "Titan"] as Spec["class"][]}>
              {(c) => (
                <button
                  role="radio"
                  aria-checked={spec.class === c}
                  onClick={() => setClass(c)}
                >
                  {c}
                </button>
              )}
            </For>
          </div>,
        )}
        {row(
          infantry() ? "Kit" : "Frame",
          <>
            {select(
              () => spec.frame,
              (v) => setSpec("frame", v),
              MATERIALS,
              MATERIAL_TIPS,
            )}
            {number(
              () => spec.frame_mass,
              (n) => setSpec("frame_mass", n),
              "t",
            )}
          </>,
        )}
        {/* What a soldier is: a person (yours, if you chose a people) or a
            robot, which needs no people but needs Robotics. */}
        <Show when={infantry()}>
          {row(
            "Body",
            <div class="segmented" role="radiogroup" aria-label="Body">
              <For each={bodies()}>
                {(b) => (
                  <button
                    role="radio"
                    aria-checked={sameBody(spec.body, b.value)}
                    aria-disabled={b.locked ? "true" : undefined}
                    data-tip={b.tip}
                    onClick={() => !b.locked && setSpec("body", b.value as never)}
                  >
                    {b.label}
                  </button>
                )}
              </For>
            </div>,
          )}
        </Show>
        <Show when={!infantry()}>
          {row(
            "Drive",
            <>
              <Show when={!titan()}>
                {select(
                  () => spec.locomotion,
                  (v) => setSpec("locomotion", v),
                  LOCOMOTION,
                  LOCOMOTION_TIPS,
                )}
              </Show>
              {select(
                () => spec.power,
                (v) => setSpec("power", v),
                POWER,
                POWER_TIPS,
              )}
              {number(
                () => spec.output,
                (n) => setSpec("output", n),
                "kW",
                10,
              )}
            </>,
          )}
        </Show>
        <div class="spec-row slots-row">
          <span class="row-label">
            Slots
            <span class="muted small">
              {" "}
              {spec.components.length} / {slots()}
            </span>
          </span>
          <div class="slots">
            <For each={spec.components}>
              {(c, k) => (
                <div class="slot">
                  <span class="slot-kind" ref={(el) => setTip(el, () => COMPONENT_TIPS[kindOf(c)] ?? "")}>
                    {KINDS.find(([id]) => id === kindOf(c))?.[1] ?? kindOf(c)}
                  </span>
                  <div class="spec-controls">{controls(c, k())}</div>
                  <button class="ghost icon slot-remove" aria-label="Remove" onClick={() => setSpec("components", (l) => l.filter((_, x) => x !== k()))}>
                    ×
                  </button>
                </div>
              )}
            </For>
            <For each={Array.from({ length: Math.max(0, slots() - spec.components.length) })}>{() => <div class="slot empty">Empty</div>}</For>
            <div class="chips slot-add">
              <For each={KINDS}>
                {([id, label, make]) => (
                  <button
                    class="chip"
                    disabled={spec.components.length >= slots()}
                    onClick={() => setSpec("components", (l) => [...l, make()])}
                    ref={(el) => deltaTip(el, `add-${id}`, COMPONENT_TIPS[id] ?? "", (s) => ({ ...s, components: [...s.components, make()] }))}
                  >
                    + {label}
                  </button>
                )}
              </For>
            </div>
          </div>
        </div>
        <Show when={infantry()}>
          {row(
            "Training",
            <For each={TRAINING}>
              {([id, label]) =>
                toggle(
                  () => spec.training.includes(id),
                  (v) =>
                    setSpec("training", (l) =>
                      v ? [...l, id] : l.filter((x) => x !== id),
                    ),
                  label,
                  (el) =>
                    deltaTip(el, `train-${id}`, (TRAINING_TIPS[id] ?? "") + TRAINING_TIME, (s) => ({
                      ...s,
                      training: s.training.includes(id) ? s.training.filter((x) => x !== id) : [...s.training, id],
                    })),
                )
              }
            </For>,
          )}
          <Show when={v31() && spec.training.length}>
            <p class="muted small design-note">Made only at a barracks with a training camp of yours within 12 hexes.</p>
          </Show>
        </Show>
        <Show when={titan()}>
          <div class="spec-row">
            <span class="row-label">Footprint</span>
            <div class="footprint-editor">
              <div class="segmented" role="radiogroup" aria-label="Brush">
                <For each={["Hull", ...LEGS]}>
                  {(b) => (
                    <button
                      role="radio"
                      aria-checked={brush() === b}
                      classList={{ planned: PLANNED_LEGS.has(b) }}
                      data-tip={LEG_TIPS[b]}
                      onClick={() => setBrush(b)}
                    >
                      {b}
                    </button>
                  )}
                </For>
              </div>
              <p class="muted small">Drill, Freight and Rail legs are planned: placing them has no effect yet.</p>
              <svg
                class="footprint"
                viewBox="-140 -120 280 240"
                onContextMenu={(e) => e.preventDefault()}
              >
                <For each={hexagon()}>
                  {([q, r]) => {
                    const [x, y] = px(q, r);
                    return (
                      <g
                        data-tip={legAt(q, r) ? LEG_TIPS[legAt(q, r)!.kind] : undefined}
                        onClick={() => paint(q, r)}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          cycleLeg(q, r);
                        }}
                      >
                        <polygon
                          points={corners(x, y)}
                          classList={{
                            tile: true,
                            on: hasTile(q, r),
                            centre: q === 0 && r === 0,
                          }}
                        />
                        <Show when={legAt(q, r)}>
                          <text
                            x={x}
                            y={y + 4}
                            text-anchor="middle"
                            class="leg"
                            classList={{ planned: PLANNED_LEGS.has(legAt(q, r)!.kind) }}
                          >
                            {legAt(q, r)!.kind[0]}
                          </text>
                        </Show>
                      </g>
                    );
                  }}
                </For>
              </svg>
            </div>
          </div>
        </Show>
      </div>

      <div class="design-preview">
        <h2>Preview</h2>
        <Show when={error()}>
          <p class="design-error">{error()}</p>
        </Show>
        <Show when={stats()}>
          <div class="stats">
            {stat("Health", stats()!.hp)}
            {stat("Speed", stats()!.speed)}
            {stat("Armour", stats()!.armor ? `${stats()!.armor} mm` : 0)}
            {stat("Damage", stats()!.damage)}
            {stat("Penetration", stats()!.pen ? `${stats()!.pen} mm` : 0)}
            {stat("Range", stats()!.range ? `${stats()!.range} hexes` : 0)}
            {stat("Sight", `${stats()!.sight} hexes`)}
            {stat("Cargo", stats()!.cargo)}
            {stat("Builds", stats()!.build ? `${stats()!.build}/tick` : 0)}
            {stat("Heals", stats()!.medic ? `${stats()!.medic} hp/tick` : 0)}
            {stat(
              "Repairs",
              stats()!.repair ? `${stats()!.repair} hp/tick` : 0,
            )}
            {stat("Mass", preview()?.mass ? `${preview()!.mass} t` : 0)}
          </div>
          <h2>Cost each</h2>
          <div class="stats">
            <For
              each={(preview()?.bill as [number, number][] | undefined) ?? []}
            >
              {([g, n]) =>
                stat(goods()[g] ?? `Good ${g}`, n.toLocaleString("en-US"))
              }
            </For>
            {stat("Crew", preview()?.crew as number)}
            {stat(
              "Build time",
              preview()?.build_pct ? `${preview()!.build_pct}%` : 0,
            )}
          </div>
        </Show>
        <Show when={needs().length}>
          <h2>Research</h2>
          <For each={needs()}>
            {(n) => (
              <div class="need" classList={{ met: has().has(n) }}>
                {has().has(n) ? "✓" : "–"} {n}
              </div>
            )}
          </For>
        </Show>
        <button
          class="button primary register"
          disabled={
            !!error() || !stats() || missing().length > 0 || !spec.name.trim() || !!sent() || !!twin()
          }
          onClick={register}
        >
          {sent() ? "Registering…" : twin() ? `Already registered as ${twin()!.name}` : "Register"}
        </button>
      </div>
    </section>
  );
}
